<template>
    <div id="content" class="tasks" :class="[{ detail: detail, portrait: ['portrait'].includes($mq) }, $mq]">
        <div class="column left hide-scrollbar" v-if="!detail">
            <div class="calendar-navigation-container" v-if="!detail">
                <calendar-navigation @select="selectDate" @calendarOpen="calendarState" :selected_date="date.format('YYYY-MM-DD')" :future_days="1" :past_days="pastDays"></calendar-navigation>
            </div>
            <!-- Category selector for landscape -->
            <div class="category-list-container" v-if="['landscape', 'desktop'].includes($mq) && showCategories">
                <ChecklistCategory v-for="(category, index) in categories" :selected="categorySelected && categorySelected.id" @selectCategory="selectCategory" :itemClasses="itemClasses(category)" :category="category" :key="index" />
            </div>
            <!-- Category name for portrait -->
            <div class="category-selected-container" v-if="categorySelected && !detail && ['portrait'].includes($mq) && showCategories">
                <SimpleNavigationBar @back-button-click="() => (categorySelected = false)" :color="categorySelected.color" :title="categorySelected.id === 'all' ? $t('tasks.all_tasks') : categorySelected.name" />
                <div class="timeframe-button" @click="timeframeHandler()" :class="{ inactive: !showTimeFrames }"></div>
            </div>
            <!-- Timeframes  -->
            <CollapseTransition :duration="transitionDuration">
                <div class="timeframes-1 hide-scrollbar slideIn" ref="tframeContainer" v-if="showSidebarTimeframes">
                    <div class="tframe" :class="[tframeClasses(tframe, index), $mq]" v-for="(tframe, index) in timeframes" :key="index" @click="toggleFrame(tframe.id)" :id="index">
                        <div class="tf-title-portrait">
                            <h6 v-if="['portrait'].includes($mq)" class="tf-title" :class="{ condensed: detail }">
                                {{ tframe.name ? tframe.name : $t('tasks.timeframe.default.title') }}
                            </h6>
                        </div>
                        <div class="graph chart">
                            <ApexRadialChart :percentage="tframe.score" :status="getStatus(tframe)" :sizeWidth="['portrait'].includes($mq) ? 140 : 100" :sizeHeight="['portrait'].includes($mq) ? 140 : 100" :condensed="['portrait'].includes($mq)" :isSelected="currentTimeframe == index" />
                        </div>
                        <div class="meta">
                            <h6 v-if="['landscape', 'desktop'].includes($mq)" class="tf-title" :class="{ condensed: detail }">
                                {{ tframe.name ? tframe.name : $t('tasks.timeframe.default.title') }}
                            </h6>

                            <div class="available" v-if="!detail">
                                <span class="hour">{{ $tc('assets.timing.from', 1) }} {{ timeFormat(tframe.start_hour) }} </span>
                                <span class="hour" v-if="tframe.end_hour">{{ $tc('assets.timing.to', 1) }} {{ timeFormat(tframe.end_hour) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </CollapseTransition>
        </div>

        <!-- CATEGORY LIST FOR PORTRAIT -->
        <div class="category-list-container hide-scrollbar" v-if="categories && !categorySelected && Object.values(categories).length > 0 && ['portrait'].includes($mq) && !detail && showCategories" :class="[$mq]">
            <ChecklistCategory @selectCategory="selectCategory" v-for="(category, index) in categories" :itemClasses="itemClasses(category)" :category="category" :key="index" />
        </div>

        <!-- MAIN -->
        <div v-if="(!detail && categorySelected) || (categories && !detail && Object.values(categories).length <= 1)" class="right hide-scrollbar" :class="[{ 'calendar-opened': calendarIsVisible }, { 'timeframe-opened': showTimeFrames }, { category: categories.length > 0 }]">
            <div class="contain">
                <!-- TIMEFRAMES ON LANDSCAPE/DESKTOP -->
                <template v-if="['landscape', 'desktop'].includes($mq) && Object.values(categories).length > 0 && showCategories">
                    <div class="timeframes-container scrollbar">
                        <div class="tframe tframe-portrait" :class="tframeClasses(tframe, index)" v-for="(tframe, index) in timeframes" :key="index" @click="toggleFrame(tframe.id)" :id="index">
                            <div class="tf-title-portrait">
                                <h6 class="tf-title" :class="{ condensed: detail }">
                                    {{ tframe.name ? tframe.name : $t('tasks.timeframe.default.title') }}
                                </h6>
                            </div>
                            <div class="graph chart">
                                <ApexRadialChart :percentage="tframe.score ? tframe.score : 0" :status="getStatus(tframe)" :isSelected="false" :sizeHeight="120" :sizeWidth="140" />
                            </div>
                            <div class="meta">
                                <div class="available" v-if="!detail">
                                    <span class="hour">{{ $tc('assets.timing.from', 1) }} {{ timeFormat(tframe.start_hour) }} </span>
                                    <span class="hour" v-if="tframe.end_hour">{{ $tc('assets.timing.to', 1) }} {{ timeFormat(tframe.end_hour) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-if="isLoadedChecklist">
                    <template v-if="checklists && checklists.length > 0">
                        <div v-if="showChecklistSearchBar" class="checklist-search-bar">
                            <input type="text" v-model="checklistSearchQuery" :placeholder="$t('tasks.search_placeholder')" class="checklist-search-input" />
                            <div v-if="checklistSearchQuery !== ''" class="checklist-search-close" @click="checklistSearchQuery = ''" role="button" :aria-label="$t('label.clear')"></div>
                        </div>
                        <div
                            class="checklist-container"
                            :class="{
                                categories: categories.length > 0 && showCategories,
                                'hide-scrollbar': ['portrait'].includes(this.$mq)
                            }"
                        >
                            <!-- LISTADO DE CHECKLISTS (título y link al checklist)-->
                            <template v-if="visibleChecklists.length > 0">
                                <item v-for="check in visibleChecklists" :item="check" :allCategories="categorySelected && categorySelected.id == 'all'" @sendId="openDetail" :key="check.id"></item>
                            </template>
                            <div v-else-if="checklistSearchQuery.trim() !== '' && filteredChecklists.length === 0" class="empty-page hide-scrollbar">
                                <EmptyPage customClass="checklist" :title="$t('empty_pages.generic_title')" :description="$t('tasks.search_no_results')"></EmptyPage>
                            </div>
                            <!-- EMPTY STATE: sin resultados de búsqueda, o listados cuyas filas no se pintan -->
                            <div v-else class="empty-page hide-scrollbar">
                                <EmptyPage customClass="checklist" :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.checklist_description')"></EmptyPage>
                            </div>
                        </div>
                    </template>
                    <!-- EMPTY STATE -->
                    <template v-else-if="checklists && checklists.length <= 0">
                        <div class="empty-page hide-scrollbar" v-if="!connectionError">
                            <EmptyPage customClass="checklist" :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.checklist_description')"></EmptyPage>
                        </div>
                        <ConnectionBox gType="checklistConnection" @reload="reloadChecklist" v-else></ConnectionBox>
                    </template>
                    <template v-else>
                        <div class="empty-page hide-scrollbar">
                            <EmptyPage customClass="checklist" :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.checklist_description')"></EmptyPage>
                        </div>
                    </template>
                </template>
            </div>
        </div>

        <!-- DETAIL -->
        <detail v-if="detail" :gId="detail" :cType="type" @setDateTimeframe="setDateTimeframe"></detail>
    </div>
</template>

<script>
import { formatHourTime } from '@/utils/dateInput'
import ChecklistCategory from '@/components/domain/tasks/checklistCategory.vue'
import EmptyPage from '@/components/layout/EmptyPage'
import item from '@/components/domain/tasks/item'
import detail from '@/components/domain/tasks/detail'
import calendar from '@/components/layout/Calendar'
import CalendarNavigation from '@/components/layout/CalendarNavigation.vue'
import { CollapseTransition } from '@ivanv/vue-collapse-transition'
import ConnectionBox from '@/components/domain/ui/Box.vue'
import SimpleNavigationBar from '@/components/domain/ui/SimpleNavigationBar.vue'
import ApexRadialChart from '@/components/domain/ui/ApexRadialChart.vue'
import { getLocationMoment } from '@/utils/locationTime'
import { FORMAT } from '@/constants'
import { getCustomApply } from '@/utils/templateCustom'

export default {
    name: 'Checklist',
    components: {
        item,
        detail,
        calendar,
        EmptyPage,
        CalendarNavigation,
        ChecklistCategory,
        CollapseTransition,
        ConnectionBox,
        SimpleNavigationBar,
        ApexRadialChart
    },

    data() {
        return {
            type: 1,
            route_name: 'ChecklistDetail',
            route: 'Checklist',
            detail: this.$route.params.id,
            // date: this.$route.params.propDate ? this.$route.params.propDate : moment().set({ minutes: 0, seconds: 0 }),
            // date: moment().set({ minutes: 0, seconds: 0 }),
            name: this.$route.params.name,
            yesterdaytf: false,
            pastDays: 1,
            calendarIsVisible: false,
            categorySelected: false,
            showTimeFrames: false,
            transitionDuration: 300,
            showCategories: true,
            isLoadedChecklist: false,
            connectionError: false,
            checklistSearchQuery: ''
        }
    },
    computed: {
        date: {
            get() {
                const value = this.$route.params.propDate || this.$store.getters['tasks/currentDate']
                const parsed = moment.isMoment(value) ? value : moment(value)
                const dateKey = parsed.isValid() ? parsed.format('YYYY-MM-DD') : getLocationMoment(this.$store.getters).format('YYYY-MM-DD')
                return moment.utc(dateKey, 'YYYY-MM-DD')
            },
            set(value) {
                this.$store.commit('tasks/setCurrentDate', value.format('YYYY-MM-DD'))
            }
        },
        def() {
            return this.$store.getters['tasks/getDefaults']
        },
        timeframes() {
            return this.$store.getters['tasks/getTimeframes']
        },
        currentTimeframe: {
            get() {
                return this.$store.getters['tasks/getCurrentTimeframe']
            },
            set(val) {
                this.$store.commit('tasks/setCurrentTimeframe', val)
            }
        },
        user_id() {
            return this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            var employee = this.$store.getters['employee/getEmployee'](this.user_id)
            if (employee) {
                return employee
            }
        },
        connection() {
            return this.$store.getters['getConnState']
        },
        checklists() {
            const checklists = this.$store.getters['tasks/getAll']
            const aReturn = []

            if (!this.user || !this.user.rol) {
                return aReturn
            }

            const rolEmployee = this.$store.getters['employee/getRol'](this.user.rol)

            this.isLoadedChecklist = true //To show the empty page only when try to load the checklist

            const selectedCategoryId = this.categorySelected ? this.categorySelected.id : null
            const hasSingleCategory = Object.values(this.categories).length <= 1
            let currentDate = this.date //.valueOf()
            for (var k in checklists) {
                const businessDate = moment.utc(checklists[k].business_date)
                if (businessDate.format('YYYY-MM-DD') == currentDate.format('YYYY-MM-DD')) {
                    // si tiene los timeframes desplegados
                    if ((this.showTimeFrames && selectedCategoryId != 'all') || hasSingleCategory) {
                        // Filtrar por la categoría seleccionada
                        if (checklists[k].category_id == selectedCategoryId || hasSingleCategory) {
                            if (checklists[k].timeframe_id == this.currentTimeframe) {
                                if (checklists[k] && checklists[k].config != false && checklists[k].config.nRoles && checklists[k].config.nRoles.length > 0) {
                                    const templateRoles = checklists[k].config.nRoles
                                    let bChecklistAdded = false
                                    templateRoles.forEach((role) => {
                                        if (role.roleId == this.user.rol) {
                                            aReturn.push(checklists[k])
                                            bChecklistAdded = true
                                        }
                                    })

                                    if (rolEmployee && rolEmployee.level >= 8 && !bChecklistAdded) {
                                        aReturn.push(checklists[k])
                                    }
                                } else if (checklists[k] && checklists[k].config != false && checklists[k].config.roles && checklists[k].config.roles.length > 0) {
                                    var templateroles = checklists[k].config.roles

                                    // Si el rol del usuario está en el listado del template o es nivel 8 se muestra
                                    if (templateroles.includes(this.user.rol) || (rolEmployee && rolEmployee.level >= 8)) {
                                        aReturn.push(checklists[k])
                                    }
                                    // SI NO TIENE NINGUN ROL SE MUESTRA
                                } else aReturn.push(checklists[k])
                            }
                        }
                    } else {
                        if (checklists[k].category_id == selectedCategoryId || selectedCategoryId == 'all') {
                            // if is portrait and there is no selected timeframe dont filter by timeframe
                            if ((['landscape', 'desktop'].includes(this.$mq) && checklists[k].timeframe_id == this.currentTimeframe) || (['portrait'].includes(this.$mq) && !this.showTimeFrames) || (['portrait'].includes(this.$mq) && this.showTimeFrames && checklists[k].timeframe_id == this.currentTimeframe)) {
                                if (checklists[k] && checklists[k].config != false && checklists[k].config.nRoles && checklists[k].config.nRoles.length > 0) {
                                    const templateRoles = checklists[k].config.nRoles
                                    let bChecklistAdded = false
                                    templateRoles.forEach((role) => {
                                        if (role.roleId == this.user.rol) {
                                            aReturn.push(checklists[k])
                                            bChecklistAdded = true
                                        }
                                    })

                                    if (rolEmployee && rolEmployee.level >= 8 && !bChecklistAdded) {
                                        aReturn.push(checklists[k])
                                    }
                                } else if (checklists[k] && checklists[k].config != false && checklists[k].config.roles && checklists[k].config.roles.length > 0) {
                                    var templateroles = checklists[k].config.roles

                                    // Si el rol del usuario está en el listado del template o es nivel 8 se muestra
                                    if (templateroles.includes(this.user.rol) || (rolEmployee && rolEmployee.level >= 8)) {
                                        aReturn.push(checklists[k])
                                    }
                                    // SI NO TIENE NINGUN ROL SE MUESTRA
                                } else aReturn.push(checklists[k])
                            }
                        }
                    }
                } else {
                    // return false
                }
            }

            aReturn.sort((a, b) => {
                const getSortKey = (item) => {
                    if (!item.limit || !item.limit.start) {
                        return 0
                    }
                    const start = moment(item.limit.start)
                    if (!start.isValid()) {
                        return 0
                    }
                    const base = item.business_date ? moment(item.business_date).startOf('day') : start.clone().startOf('day')
                    if (!base.isValid()) {
                        return 0
                    }
                    const timeOfDayMs = start.diff(start.clone().startOf('day'))
                    const key = base.valueOf() + timeOfDayMs
                    return Number.isFinite(key) ? key : 0
                }
                const keyA = getSortKey(a)
                const keyB = getSortKey(b)
                if (keyA !== keyB) {
                    return keyA - keyB
                }
                const nameA = String(a.title || '').toLowerCase()
                const nameB = String(b.title || '').toLowerCase()
                const locale = (this.$i18n && this.$i18n.locale) || 'en'
                const sortLocale = (locale && locale !== 'init' ? locale : 'en') || 'en'
                return nameA.localeCompare(nameB, sortLocale)
            })

            return aReturn
        },
        showChecklistSearchBar() {
            const threshold = 6
            return this.checklists.length > threshold || (this.checklistSearchQuery && this.checklistSearchQuery.trim().length > 0)
        },
        filteredChecklists() {
            const query = this.checklistSearchQuery && this.checklistSearchQuery.trim()
            if (!query) {
                return this.checklists
            }
            const normalizedQuery = query.toLowerCase()
            return this.checklists.filter((check) => {
                const title = (check.title && String(check.title)) || ''
                return title.toLowerCase().includes(normalizedQuery)
            })
        },
        visibleChecklists() {
            if (!this.filteredChecklists) {
                return []
            }
            return this.filteredChecklists.filter((checklist) => this.isChecklistVisible(checklist))
        },
        categories() {
            var checklists = this.$store.getters['tasks/getAll']
            var categories = this.$store.getters['tasks/getCategories']()
            var hashCategories = ['all']

            if (!this.user || !this.user.rol) {
                return []
            }

            var rolEmployee = this.$store.getters['employee/getRol'](this.user.rol)
            if (!rolEmployee) {
                return []
            }

            for (var k in checklists) {
                var checklist = checklists[k]
                if (checklist.category_id != false && !hashCategories.includes(checklist.category_id)) {
                    let nRoles = []
                    if (checklist.config && checklist.config.nRoles) {
                        nRoles = checklist.config.nRoles.map((rol) => rol.roleId)
                    }

                    const hasConfig = checklist.config
                    const hasRoles = hasConfig && checklist.config.roles && checklist.config.roles.length > 0
                    const hasNRoles = hasConfig && checklist.config.nRoles && checklist.config.nRoles.length > 0
                    const userRoleInRoles = hasRoles && checklist.config.roles.includes(this.user.rol)
                    const userRoleInNRoles = hasNRoles && nRoles.includes(this.user.rol)
                    const isHighLevelEmployee = rolEmployee && rolEmployee.level >= 8
                    if ((!hasRoles && !hasNRoles) || userRoleInRoles || userRoleInNRoles || isHighLevelEmployee) {
                        hashCategories.push(checklist.category_id)
                    }
                }
            }

            categories = Object.values(categories).filter((category) => {
                return hashCategories.includes(category.id)
            })

            if (!this.hasToShowCategories(categories, checklists)) {
                // remove the category that is not all
                categories = categories.filter((category) => {
                    return category.id == 'all'
                })
            }

            // sort by id = 'all first'
            return Object.values(categories).sort((a, b) => {
                return a.id == 'all' ? -1 : b.id == 'all' ? 1 : a.id - b.id
            })
        },
        showSidebarTimeframes() {
            const isPortrait = ['portrait'].includes(this.$mq)
            const portraitWithSelectedCategory = this.categories && this.categorySelected && this.showTimeFrames && isPortrait
            const portraitDetail = this.detail && isPortrait
            const hasNoCategories = Object.values(this.categories).length < 1
            const categoriesHidden = !this.showCategories

            return Boolean(portraitWithSelectedCategory || portraitDetail || hasNoCategories || categoriesHidden)
        }
    },
    methods: {
        getStatus(tframe) {
            if (tframe.score == 100 && !tframe.alert_pending_count) {
                return 'done'
            } else if (tframe.alert_pending_count && !this.expiredTFrame(tframe)) {
                if (!this.expiredTFrame(tframe)) {
                    return 'justified'
                } else {
                    return 'uncompleted'
                }
            } else {
                if (tframe.start_hour && this.expiredTFrame(tframe)) {
                    return 'justified'
                } else {
                    return 'progress'
                }
            }
        },
        hasToShowCategories(categories, checklists) {
            // if all items doesnt have a category dont show categories
            let haveCategoryAssigned = false
            Object.values(checklists).forEach((element) => {
                if (element.category_id) haveCategoryAssigned = true
            })
            if (!haveCategoryAssigned) {
                this.showCategories = false
                return false
            }
            // if there is only one category a part of all and all checklist have the same category_id dont show categories
            if (Object.values(categories).length == 2) {
                let result = false
                Object.values(checklists).forEach((element) => {
                    if (!element.category_id) result = true
                    if (result) return

                    // get id of category that is not all
                    if (element.category_id && element.category_id != 'all') {
                        result = element.category_id == categories[1].id
                    }
                })
                this.showCategories = result
                return result
            }
            this.showCategories = true
            return true
        },
        isChecklistVisible(checklist) {
            if (!checklist || !checklist.items) {
                return true
            }
            const itemsToExclude = [FORMAT.TITLE, FORMAT.SUBTITLE]
            return Object.values(checklist.items).some((subItem) => {
                return !itemsToExclude.includes(Number(subItem.type)) && getCustomApply(subItem) !== 0
            })
        },
        timeFormat(time) {
            return formatHourTime(time, this.$i18n.locale)
        },
        selectCategory(category) {
            this.categorySelected = category
        },
        calendarState(show) {
            this.calendarIsVisible = show
        },
        selectDate(date, refresh = false) {
            log('selectDate ->', date, refresh)
            const dateKey = moment.isMoment(date) ? date.format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD')
            this.date = moment.utc(dateKey, 'YYYY-MM-DD').startOf('day')
            this.load(refresh)
            this.connectionError = !fnCheckConnection()
        },
        setDateTimeframe(date, timeframe) {
            this.date = moment.utc(date).startOf('day')
            this.currentTimeframe = timeframe
        },
        isToday() {
            return this.date.format('YYYY-MM-DD') == getLocationMoment(this.$store.getters).format('YYYY-MM-DD')
        },

        openDetail(id) {
            this.$router.push({ name: this.route_name, params: { id: id } })
        },

        timeframeHandler() {
            if (this.showTimeFrames) {
                this.showTimeFrames = false
                // save variable in localstorage
                localStorage.setItem('showTimeFrames_' + this.user_id, false)
                // this.currentTimeframe = false
            } else {
                this.showTimeFrames = true
                // save variable in localstorage
                localStorage.setItem('showTimeFrames_' + this.user_id, true)
            }
        },

        tframeClasses(tframe, index) {
            return {
                'alert pending': tframe.alert_pending_show,
                'alert important': tframe.alert_important_show,
                selected: this.currentTimeframe == index,
                expired: this.expiredTFrame(tframe) && this.isToday()
            }
        },

        itemClasses(tframe) {
            if (tframe) {
                if (tframe.score == 100 && !tframe.alert_pending_count) {
                    return 'done'
                } else if (tframe.alert_pending_count && !this.expiredTFrame(tframe)) {
                    if (!this.expiredTFrame(tframe)) {
                        return 'justified'
                    } else {
                        return 'uncompleted'
                    }
                } else {
                    if (tframe.start_hour && this.expiredTFrame(tframe)) {
                        return 'justified'
                    } else {
                        return 'progress'
                    }
                }
            }
        },

        toggleFrame(id) {
            log(id)
            if (id || id == 0) {
                this.currentTimeframe = id
            } else {
                id = this.currentTimeframe
            }

            if (this.detail) {
                this.detail = false
                this.$router.push({
                    name: this.route,
                    params: { timeframe_id: id }
                })
            }
        },

        expiredTFrame(tframe) {
            if ((!tframe.name && this.isToday()) || (!this.isToday() && !this.yesterdaytf)) {
                return false
            }

            var now = getLocationMoment(this.$store.getters)
            if (tframe.start_hour) {
                var start = moment.utc(this.date).set('hour', tframe.start_hour).set('minute', 0).set('second', 0).set('millisecond', 0)
                var endHour = tframe.end_hour < 24 ? tframe.end_hour : tframe.end_hour - 24
                var end = moment.utc(this.date).set('hour', endHour).set('minute', 0).set('second', 0).set('millisecond', 0)
                if (tframe.end_hour >= 24) end.add(1, 'days')

                return !now.isBetween(start, end, 'hour') && now.isAfter(end)
            }
        },

        scrollTo(id) {
            if (['portrait'].includes(this.$mq)) {
                this.$refs.tframeContainer.scrollLeft = document.getElementById(id).offsetLeft - window.innerWidth / 2 + document.getElementById(id).offsetWidth / 2 + 10 // plus 10px of margin
            } else {
                var scroll = {
                    container: this.$refs.tframeContainer,
                    target: document.getElementById(id),
                    offset: -100
                }
                this.$root.scrollTo(scroll)
            }
        },

        load(refresh = true) {
            var self = this
            const locationNow = getLocationMoment(this.$store.getters)

            // var categories = this.categories

            if (!this.$store.getters['tasks/userDateChanged']) {
                if (this.date && moment.utc(this.date).format(this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY') == locationNow.format(this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY') && this.timeframes && Object.keys(this.timeframes).length > 0 && Object.values(this.timeframes)[Object.keys(this.timeframes).length - 1].end_hour > 24 && Object.values(this.timeframes)[Object.keys(this.timeframes).length - 1].end_hour - 24 > parseInt(locationNow.format('HH'))) {
                    this.yesterdaytf = true
                    this.selectDate(locationNow.clone().add(-1, 'days').format('YYYY-MM-DD'), true)
                }
            } else {
                if (
                    moment.utc(this.date).format(this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY') ==
                        locationNow
                            .clone()
                            .add(-1, 'days')
                            .format(this.$store.getters['login/getConfig'].dateFormat || 'DD/MM/YYYY') &&
                    this.timeframes &&
                    Object.keys(this.timeframes).length > 0 &&
                    Object.values(this.timeframes)[Object.keys(this.timeframes).length - 1].end_hour > 24 &&
                    Object.values(this.timeframes)[Object.keys(this.timeframes).length - 1].end_hour - 24 > parseInt(getLocationMoment(this.$store.getters).format('HH'))
                ) {
                    this.yesterdaytf = true
                }
            }

            self.$overlay.loading()
            if (self.$route.params.id == undefined) {
                self.$store.dispatch('tasks/loadCategories').then(function () {
                    self.$store
                        .dispatch('tasks/loadAll', {
                            date: self.date.format('YYYY-MM-DD'),
                            forceUpdate: true
                        })
                        .then(function () {
                            if (['landscape', 'desktop'].includes(self.$mq)) {
                                if (Object.values(self.categories).length > 0 && !self.categorySelected) {
                                    for (var category in self.categories) {
                                        var idx = self.categories[category]
                                        if (idx.id == 'all' && self.showCategories) {
                                            self.categorySelected = idx
                                        }
                                    }
                                }
                            }
                            if (refresh) {
                                self.$bar.show()
                                self.$bar.reset()

                                // Set Timeframe
                                var desired = self.$route.params.timeframe_id ? self.$route.params.timeframe_id : false
                                if (self.$route.params.timeframe_id == 0) {
                                    desired = '0'
                                }

                                if (!desired) {
                                    var withAlert = self.$route.params.withAlert ? self.$route.params.withAlert : false
                                    for (var i in self.timeframes) {
                                        let tf = self.timeframes[i]
                                        if (tf.name) {
                                            let aux = moment
                                                .utc(self.date)
                                                .set({
                                                    hours: 0,
                                                    minutes: 0,
                                                    seconds: 0
                                                })
                                                .add(tf.end_hour, 'hours')

                                            if (withAlert && tf[withAlert]) {
                                                desired = tf.id
                                                break
                                            } else if (moment.utc(moment.utc(self.date).format('YYYY-MM-DD') + ' ' + locationNow.format('HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss').isBefore(aux) && !self.expiredTFrame(tf)) {
                                                desired = tf.id
                                                break
                                            }
                                        }
                                    }
                                }

                                if (desired && !self.detail) {
                                    self.currentTimeframe = desired
                                } else if (Object.keys(self.timeframes).length >= 1 && !self.detail) {
                                    self.currentTimeframe = self.timeframes[0] ? self.timeframes[0].id : self.timeframes[Object.keys(self.timeframes)[0]].id
                                }

                                if (self.currentTimeframe && !self.detail) {
                                    // TODO Uncomment this line when the app is ready to be used
                                    // self.scrollTo(self.currentTimeframe)
                                }

                                self.detail = false
                            }
                            self.$overlay.hide()
                            self.isLoadedChecklist = true
                        })
                })
            } else if (self.$route.params.id != undefined) {
                self.$store
                    .dispatch('tasks/loadById', {
                        id: self.$route.params.id
                    })
                    .then(function () {
                        self.$store
                            .dispatch('tasks/loadCategories')
                            .then(function () {
                                self.detail = self.$route.params.id

                                for (let key in self.checklists) {
                                    const checklist = self.checklists[key]
                                    if (self.$route.params.id == checklist.id) {
                                        self.currentTimeframe = checklist.timeframe_id ? checklist.timeframe_id : 0
                                    }
                                }

                                self.$overlay.hide()
                                self.isLoadedChecklist = true
                            })
                            .catch(function (error) {
                                logError(error)
                                self.$overlay.hide()
                                self.$popup.alert({
                                    closable: false,
                                    blocking: false,
                                    message: self.$t('popup.checklist.offline_open'),
                                    textSave: self.$t('popup.audits.exit'),
                                    callSave: function (response) {
                                        self.$popup.close()
                                    }
                                })
                            })
                    })
            }

            // this.isLoadedChecklist = true
        },
        reloadChecklist() {
            this.load()
        }
    },
    filters: {
        hour: function (value) {
            if (value == parseInt(value, 10)) {
                return moment('00:00', 'HH:mm').add(value, 'hours').format('HH')
            } else {
                return moment('00:00', 'HH:mm').add(value, 'hours').format('HH:mm')
            }
        }
    },
    mounted() {
        // get timeframe variable from localstorage
        if (localStorage.getItem('showTimeFrames_' + this.user_id) !== null) {
            localStorage.getItem('showTimeFrames_' + this.user_id) == 'false' ? (this.showTimeFrames = false) : (this.showTimeFrames = true)
        }
    },
    created() {
        this.$bar.reset()
        if (!this.$route.params.propDate && !this.$store.getters['tasks/userDateChanged']) {
            this.$store.commit('tasks/setCurrentDateFromLocation', getLocationMoment(this.$store.getters).format('YYYY-MM-DD'))
        }
        this.load()
    },
    updated() {
        this.pastDays = ['portrait'].includes(this.$mq) ? 2 : 1
        // Delete calendar open dom
        if (this.$route.name == 'ChecklistDetail') {
            this.calendarIsVisible = false
        }
    },
    watch: {
        $mq(newValue, oldValue) {
            // if is in portrait and didn't select a category, automatically select all categories for landscape/dektop
            if (((oldValue == 'portrait' && newValue == 'landscape') || oldValue == 'desktop') && !this.categorySelected) {
                for (var category in this.categories) {
                    var idx = this.categories[category]
                    if (idx.id == 'all') {
                        this.categorySelected = idx
                    }
                }
            }
        },
        $route: 'load',
        categorySelected(newValue, oldValue) {
            if (!newValue) {
                this.transitionDuration = 0
            } else {
                this.transitionDuration = 300
            }
        }
    }
}
</script>

<style lang="scss">
#content.tasks {
    $column-width: 20%;
    $column-max-width: 180px;
    // $content-width: 100%;

    .slide-leave-active,
    .slide-enter-active {
        transition: 0.3s;
    }
    .slide-enter {
        transform: translate(90%, 0);
        position: absolute;
    }
    .slide-leave-to {
        position: absolute;
        transform: translate(-90%, 0);
    }

    > .right {
        $content-margin: 12px;

        padding-top: 16px;
        // margin: $content-margin 0 0 0;
        height: calc(100% - 25px) !important;
        border: none;
        width: calc(70% - 320px);
        min-width: calc(100% - 320px);
        float: right;

        // @include border-radius($default-border-radius);
        // @include default-box-shadow();
        // background-color: $default-bg-color !important;
        // width: calc(100% - 340px);
        // min-width: calc(100% - #{$column-max-width}) !important;

        &.empty {
            @include display-flex();
            @include align-items();
            @include justify-content(flex-end);
            width: 100% !important;

            .message {
                @include display-inline-flex();
                @include align-items();
                @include background($image: img('alexpose_04.svg'), $size: contain, $position: right center);
                width: 100%;
                padding-right: 150px;
                height: 250px;
                max-height: 100%;
            }
        }

        .contain {
            width: 98%;
            height: 100%;
            margin: 0 16px;

            .empty {
                @include empty();
                @include background($size: 70px, $position: top center);
                padding-top: 80px;
                margin-top: calc(40% - #{$menu-top-height} - 70px);
                background-image: img('confeti_light.svg');
            }

            .timeframes-container {
                @include display-flex();
                width: 100%;
                height: auto;
                margin-bottom: 20px;
                overflow-x: auto;

                .tframe {
                    @include interaction();
                    @include display-flex();
                    @include flex-direction(column);
                    @include align-items(center);
                    @include bgHover($default-bg-color, true, 15%);
                    border-radius: 8px !important;
                    width: 100%;
                    height: 120px;
                    width: auto;
                    min-width: 155px;
                    margin-right: 8px;

                    .tf-title-portrait {
                        // rompe el centrado heredado de align-items:center en .tframe
                        align-self: flex-start;
                        display: flex;
                        justify-content: flex-start;
                        width: 100%;
                        padding-left: 8px; // opcional, para respirar un poco
                        text-align: left;

                        h6,
                        .tf-title {
                            text-align: left !important;
                            margin: 0;
                        }
                    }

                    .graph.chart {
                        $chart-size: rem(65px);
                        float: none;
                        margin: 0;
                        padding: 0;
                        width: $chart-size;
                        height: $chart-size;
                        display: inline-block;
                        margin-top: 10px;
                        margin-left: -70px;

                        .c100 {
                            margin: 0;
                        }
                        span {
                            @include font-size(ml);
                            font-family: $conden;
                            height: $chart-size;
                            line-height: $chart-size;
                        }
                    }
                    .meta {
                        width: 100%;
                        text-align-last: center;
                    }

                    .tf-title {
                        @include font-size(m);
                        @include text-ellipsis();
                        font-family: $conden-bold !important;
                        margin: 0;
                        padding: 0;
                        margin-top: 3px;
                    }
                    .available {
                        margin-top: -6px;
                        .hour {
                            @include font-size(s, '', true);
                            font-family: $conden;
                        }
                    }

                    &.alert {
                        position: relative;

                        &:before {
                            @include background($color: $main, $size: 14px);
                            @include border-radius(3px);
                            content: '';
                            position: absolute;
                            display: block;
                            min-width: 20px;
                            width: auto;
                            height: 20px;
                            z-index: 2;
                            top: 5px;
                            left: 5px;
                        }

                        &.tframe-portrait:before {
                            position: absolute;
                            top: 80% !important;
                            left: 85% !important;
                        }

                        &.portrait:before {
                            position: absolute;
                            top: 80% !important;
                            left: 85% !important;
                        }

                        &.pending:before {
                            background-color: $warning;
                            background-image: img('exclamation_light.svg');
                        }
                        &.important:before {
                            background-color: $error;
                            background-image: img('exclamation_light.svg');
                        }
                    }

                    &.expired {
                        @include opacity(0.6);
                    }

                    &.selected {
                        background-color: #fff;
                        @include bgHover(#fff, false, 1.5%);
                    }
                }
            }
            .checklist-search-bar {
                position: relative;
                @include background($image: img('search_neutro.svg'), $position: left 15px center, $size: 25px);
                background-color: #fff;
                @include border-radius($default-border-radius);
                width: 100%;
                margin-bottom: 12px;
                min-height: 45px;

                .checklist-search-input {
                    width: calc(100% - 50px);
                    border: none;
                    padding: 0 40px 0 45px;
                    height: 45px;
                    margin: 0;
                    font-family: $text-light;
                    color: $neutro-s80;
                    cursor: text;
                    @include font-size(sm);
                    background: transparent;
                }

                .checklist-search-close {
                    position: absolute;
                    top: 50%;
                    right: 8px;
                    transform: translateY(-50%);
                    @include border-radius(4px);
                    @include background($image: img('close_dark.svg'), $size: 12px, $position: center center);
                    width: 33px;
                    height: 33px;
                    background-color: $neutro-t50;
                    cursor: pointer;
                    @include interaction();
                }
            }
            .checklist-container {
                width: 100%;
                // height: calc(100% - 139px);
                overflow: auto;
                height: 100%;

                &.categories {
                    height: calc(100% - 139px);
                    padding-right: 8px;
                }
            }
        }

        .empty-page {
            width: 100%;
            height: calc(100% - 170px);
        }
    }

    .category-list-container {
        @include display-flex();
        @include flex-direction(column);
        gap: 4px;
        width: 100%;
        height: auto;
        // padding-top: 6px;

        &.portrait {
            height: calc(100% - 73px);
            overflow-y: auto;
        }
    }

    > .column.left {
        $content-space: 6px;
        width: 100%;
        box-shadow: 0px 0px 10px rgba(159, 162, 166, 0.25);
        border-radius: 8px;
        background-color: #fff;
        padding-bottom: 0 !important;
        height: calc(100%);
        min-width: 320px;

        .calendar-navigation-container {
            display: block;
            width: 100%;
            height: auto;
            padding: 8px 8px 16px 8px;
        }

        .category-selected-container {
            position: relative;
            width: 100%;
            max-width: 100%;
            height: auto;
            @include display-flex();
            @include justify-content(space-between);
            @include align-items(center);

            .category-name {
                @include display-flex();
                @include align-items(center);
                width: calc(100% - 85px);
                padding-left: 6px;
                .category-color {
                    width: 13px;
                    height: 13px;
                    min-height: 13px;
                    min-width: 13px;
                    border-radius: 50%;
                }
                .text {
                    @include font-size(m);
                    @include text-ellipsis();
                    font-family: $text-bold;
                    padding-left: 6px;
                    color: $neutro-s80;
                }
            }

            .timeframe-button {
                position: relative;
                @include background($image: img('timeframe_icon.svg'), $size: 20px);
                @include border-radius(4px);
                background-color: $main-t80;
                width: 40px;
                height: 40px;
                min-height: 40px;
                min-width: 40px;
                cursor: pointer;

                &:hover {
                    @include bgHover($main-t80);
                }

                &.inactive {
                    @include background($image: img('timeframe_icon_ffffff.svg'), $size: 20px);
                    background-color: #fff;
                    &:hover {
                        @include bgHover(#fff);
                    }
                }
            }
        }

        .timeframes-1 {
            padding: $content-space $content-space $content-space $content-space;
            height: auto;
            overflow: auto;

            .tframe {
                @include interaction();
                border-radius: 4px !important;
                display: block;
                width: 100%;
                height: auto;
                width: calc(100%);
                text-align: center;
                margin-bottom: 8px !important;

                @include bgHover(transparent, true, 15%);

                .graph.chart {
                    $chart-size: rem(80px);
                    float: none;
                    margin: 0;
                    padding: 0;
                    padding-top: 14px;
                    width: $chart-size;
                    height: auto;
                    display: inline-block;
                    position: relative;
                    top: 0px;
                    left: 2px;

                    .c100 {
                        margin: 0;
                    }
                    span {
                        @include font-size(ml);
                        font-family: $conden;
                        height: $chart-size;
                        line-height: $chart-size;
                    }
                }

                .tf-title {
                    @include font-size(sm);
                    @include text-ellipsis();
                    font-family: $conden-bold !important;
                    margin: 0;
                    padding: 0;
                    margin-top: 7px;
                    margin-bottom: 7px;
                }
                .available {
                    margin-top: 3px;
                    .hour {
                        @include font-size(s);
                        font-family: $conden;
                    }
                }

                &.alert {
                    position: relative;

                    &:before {
                        @include background($color: $main, $size: 14px);
                        @include border-radius(3px);
                        content: '';
                        position: absolute;
                        display: block;
                        min-width: 20px;
                        width: auto;
                        height: 20px;
                        z-index: 2;
                        top: 5px;
                        left: 5px;
                    }

                    &.portrait:before {
                        position: absolute;
                        top: 80% !important;
                        left: 85% !important;
                    }

                    &.pending:before {
                        background-color: $warning;
                        background-image: img('exclamation_light.svg');
                    }
                    &.important:before {
                        background-color: $error;
                        background-image: img('exclamation_light.svg');
                    }
                }

                &.expired {
                    @include opacity(0.6);
                }

                &.selected {
                    @include default-box-shadow();
                    background-color: $main;
                    border-radius: 4px;

                    &.landscape,
                    &.desktop {
                        .meta {
                            .tf-title {
                                color: #fff;
                            }
                            .available {
                                .hour {
                                    color: #fff;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    &.landscape:not(.detail),
    &.desktop:not(.detail) {
        .left {
            .timeframes-1 {
                .tframe {
                    @include display-flex();
                    @include align-content(center);
                    @include justify-content(flex-start);
                    @include align-items(center);

                    .meta {
                        padding-left: 25px;
                    }
                }
            }
        }
    }
}
</style>

<!-- // PORTRAIT STYLES -->
<style lang="scss" scoped>
#content.portrait {
    padding: 8px;

    .column.left {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0;
        margin-top: 0;
        height: auto !important;
        background-color: #f4f2ed !important;
        @include border-radius(4px);
        box-shadow: unset;

        .calendar-navigation-container {
            padding: 0px 0px 16px 0px;

            .calendar-navigation {
                > .calendar-picker {
                    width: 100px !important;
                    max-width: 500px !important;
                }
            }
        }

        .timeframes-1 {
            padding: 8px 0 0 0;
            @include border-radius(4px);
            overflow-x: auto;
            overflow-y: hidden;
            white-space: nowrap;
            height: auto;

            .tframe {
                box-shadow: none;
                background-color: transparent;
                max-width: 150px;
                display: inline-block;
                padding: 8px;
                margin: 0;
                margin-right: 5px;
                height: 120px;

                .graph.chart {
                    transform: scale(0.8);
                    margin-top: -30px;
                    margin-left: -40px;
                    margin-bottom: 6px;
                }

                .meta {
                    height: 80px;
                    display: block;
                    width: 100%;
                }

                .tf-title {
                    @include font-size(s);
                    margin-top: -5px;
                    text-align: center;
                }
                .available {
                    margin: 0;
                    margin-top: -20px;
                    text-align: center;
                    .hour {
                        @include font-size(s, '', true);
                        font-weight: 400;
                    }
                }
                &.selected {
                    box-shadow: none;
                    background-color: #fff;
                    // @include bgHover($default-bg-color, false, 1.5%);
                }
            }

            @include portrait-tablet {
                .tframe {
                    height: 130px;

                    .available {
                        .hour {
                            @include font-size(s);
                            line-height: 35px;
                        }
                    }
                }
            }
        }
    }
    .right {
        width: 100% !important;
        max-width: 100% !important;
        height: calc(100% - 230px) !important;
        box-shadow: none;
        background: transparent !important;
        margin: 0;
        padding: 0 !important;
        margin-top: 16px !important;

        .contain {
            width: 100%;
            height: 100%;
            margin: 0;

            .checklist-container {
                width: 100%;
                height: calc(100% - 10px) !important;
                overflow: auto;
                &.categories {
                    padding-right: 0px;
                }
            }
        }
        &.calendar-opened {
            height: calc(100% - 505px) !important;
        }
        &.timeframe-opened {
            height: calc(100% - 271px) !important;
        }
        .empty-page {
            width: 100%;
            height: calc(100% - 130.5px);
        }
        &.category {
            height: calc(100% - 140px) !important;
        }
    }
}

#content.portrait.detail {
    .timeframes-1 {
        display: none !important;
    }
}

/* Estilo para tf-title-portrait en modo portrait */
.tf-title-portrait {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: var(--color-black, #222);
    padding-top: 8px;

    h6,
    .tf-title {
        color: var(--color-black, #222);
        @include font-size(sm, '', true);
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        text-align: left !important;
        margin: 0;
        padding: 0;
        width: 100%;
    }
}
</style>
